import { CloudDone, CloudOff, TaskAltOutlined } from "@mui/icons-material";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { RefreshData } from "components/helper";
import ViewClientProfile from "components/Clients/ViewClientProfile";
import { IoArrowBack } from "react-icons/io5";
import { MdSave } from "react-icons/md";
import { Send } from "@mui/icons-material";

const AutoSaveStatus = ({ status }) => {
  if (!status || status === "idle") return null;

  const config = {
    dirty: {
      icon: <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />,
      label: "Unsaved",
      className: "text-amber-700 bg-amber-50 border-amber-200",
    },
    saving: {
      icon: <CircularProgress size={12} thickness={5} />,
      label: "Saving…",
      className: "text-app-text bg-app-surface-muted border-app-border",
    },
    saved: {
      icon: <CloudDone fontSize="small" className="text-emerald-600" />,
      label: "Saved",
      className: "text-emerald-700 bg-emerald-50 border-emerald-200",
    },
    error: {
      icon: <CloudOff fontSize="small" className="text-rose-600" />,
      label: "Save failed",
      className: "text-rose-700 bg-rose-50 border-rose-200",
    },
  }[status];

  if (!config) return null;

  return (
    <Tooltip
      title={
        status === "error"
          ? "Auto-save failed. Use Save to retry."
          : status === "saving"
          ? "Saving your changes..."
          : status === "dirty"
          ? "Auto-saving in a moment..."
          : "Your changes are safely saved."
      }
      arrow
    >
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
    </Tooltip>
  );
};

const AccountEditorHeader = ({
  clientId,
  clientName,
  onBack,
  onRefresh,
  autoSaveStatus,
  pendingCount = 0,
  canReadVerifications,
  onOpenVerifications,
  onSave,
  isSaving,
  showSendToProvider,
  onSendToProvider,
}) => (
  <div className="rounded-t mb-0 px-4 py-3 border-b border-app-border">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <Tooltip title="Back to accounts">
          <IconButton size="small" onClick={onBack} aria-label="Back to accounts">
            <IoArrowBack className="text-xl" />
          </IconButton>
        </Tooltip>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
            Client account
          </p>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h1 className="text-xl font-bold text-app-text m-0 truncate">
              {clientId ? (
                <ViewClientProfile
                  clientId={clientId}
                  clientName={clientName}
                  variant="link"
                  displayName={clientName || "Client"}
                  className="text-xl font-bold"
                />
              ) : (
                clientName || "Client"
              )}
            </h1>
            <RefreshData refreshData={onRefresh} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap justify-end">
        <AutoSaveStatus status={autoSaveStatus} />
        {canReadVerifications && pendingCount > 0 && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<TaskAltOutlined />}
            onClick={onOpenVerifications}
          >
            Verifications ({pendingCount})
          </Button>
        )}
        {showSendToProvider && (
          <Button variant="outlined" size="small" startIcon={<Send />} onClick={onSendToProvider}>
            Send to provider
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} sx={{ color: "white" }} /> : <MdSave />}
        >
          Save
        </Button>
      </div>
    </div>
  </div>
);

export default AccountEditorHeader;
export { AutoSaveStatus };
